import { useState, useEffect, useCallback } from 'react';
import {
  Share2, ZoomIn, ZoomOut, Maximize2,
  Database, Package, GitBranch, Globe, Cpu,
  ChevronRight, X, MessageSquare,
  Loader2, Plus, RefreshCw, ChevronDown,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useTheme } from '../lib/theme';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('cp_token')}` };
}

interface Repo {
  _id: string;
  name: string;
  url: string;
  branch: string;
  provider: string;
  indexingStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  lastIndexedAt?: string;
  fileCount?: number;
}

interface ArchService {
  id: string;
  name: string;
  type: 'service' | 'frontend' | 'api' | 'worker' | 'library' | 'app';
  lang: string;
  dirPath: string;
  framework?: string;
}

interface ArchEdge { from: string; to: string; type: 'sync' | 'async'; label?: string }
interface ArchData { services: ArchService[]; edges: ArchEdge[]; adrs: any[]; analyzed: boolean }

type NodeType = 'service' | 'database' | 'queue' | 'api' | 'external' | 'cache' | 'frontend' | 'worker' | 'library' | 'app';

interface GraphNode {
  id: string;
  label: string;
  type: NodeType;
  x: number;
  y: number;
  lang?: string;
  framework?: string;
  dirPath?: string;
}

const TYPE_CONFIG: Record<string, { color: string; bg: string; border: string; icon: React.ElementType; label: string }> = {
  service:  { color: '#818CF8', bg: '#818CF814', border: '#818CF830', icon: Cpu,      label: 'Service' },
  app:      { color: '#818CF8', bg: '#818CF814', border: '#818CF830', icon: Cpu,      label: 'App' },
  database: { color: '#34D399', bg: '#34D39914', border: '#34D39930', icon: Database, label: 'Database' },
  queue:    { color: '#FBBF24', bg: '#FBBF2414', border: '#FBBF2430', icon: Package,  label: 'Queue' },
  api:      { color: '#60A5FA', bg: '#60A5FA14', border: '#60A5FA30', icon: Globe,    label: 'API' },
  external: { color: '#F472B6', bg: '#F472B614', border: '#F472B630', icon: Globe,    label: 'External' },
  cache:    { color: '#A78BFA', bg: '#A78BFA14', border: '#A78BFA30', icon: Cpu,      label: 'Cache' },
  frontend: { color: '#38BDF8', bg: '#38BDF814', border: '#38BDF830', icon: Globe,    label: 'Frontend' },
  worker:   { color: '#FB923C', bg: '#FB923C14', border: '#FB923C30', icon: Package,  label: 'Worker' },
  library:  { color: '#A3A3A3', bg: '#A3A3A314', border: '#A3A3A330', icon: GitBranch,label: 'Library' },
};

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] ?? TYPE_CONFIG['service'];
}

function layoutNodes(services: ArchService[]): GraphNode[] {
  if (services.length === 0) return [];
  const cx = 380, cy = 270;
  const r = services.length === 1 ? 0 : Math.min(200, 75 * Math.sqrt(services.length));
  return services.map((s, i) => {
    const angle = services.length === 1 ? 0 : (i / services.length) * 2 * Math.PI - Math.PI / 2;
    return {
      id: s.id,
      label: s.name,
      type: s.type as NodeType,
      x: services.length === 1 ? cx : Math.round(cx + r * Math.cos(angle)),
      y: services.length === 1 ? cy : Math.round(cy + r * Math.sin(angle)),
      lang: s.lang,
      framework: s.framework,
      dirPath: s.dirPath,
    };
  });
}

function NodeCard({ node, selected, onClick }: { node: GraphNode; selected: boolean; onClick: () => void }) {
  const tc = getTypeConfig(node.type);
  const Icon = tc.icon;
  return (
    <div
      onClick={onClick}
      className={cn('absolute flex flex-col items-center cursor-pointer group transition-all duration-150 select-none', selected ? 'z-10' : 'z-0')}
      style={{ left: node.x, top: node.y, transform: 'translateX(-50%)' }}
    >
      <div
        className={cn('flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border transition-all', selected ? 'scale-110' : 'group-hover:scale-105')}
        style={{
          backgroundColor: selected ? tc.bg : `${tc.color}0A`,
          borderColor: selected ? tc.color : tc.border,
          boxShadow: selected ? `0 0 0 2px ${tc.color}30, 0 4px 12px rgba(0,0,0,0.15)` : undefined,
        }}
      >
        <Icon className="w-4 h-4" style={{ color: tc.color }} />
        <span className="text-[10px] font-semibold max-w-[100px] truncate text-center" style={{ color: tc.color }}>
          {node.label.includes('/') ? node.label.split('/').pop() : node.label}
        </span>
        {node.lang && <span className="text-[9px] text-t3">{node.lang.split(' · ')[0]}</span>}
      </div>
    </div>
  );
}

function DetailPanel({ node, repoId, onClose }: { node: GraphNode; repoId: string; onClose: () => void }) {
  const tc = getTypeConfig(node.type);
  return (
    <div className="w-[280px] flex-shrink-0 flex flex-col border-l" style={{ backgroundColor: 'var(--s0)', borderColor: 'var(--b-subtle)' }}>
      <div className="px-4 py-3.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--b-subtle)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc.bg }}>
            <tc.icon className="w-3 h-3" style={{ color: tc.color }} />
          </div>
          <span className="text-sm font-semibold text-t1 truncate">{node.label}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded text-t3 hover:text-t1 transition-colors flex-shrink-0"
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-2.5">
          {[
            { label: 'Type',      value: tc.label },
            { label: 'Language',  value: node.lang ?? '—' },
            { label: 'Path',      value: node.dirPath ?? '.' },
          ].map(r => (
            <div key={r.label} className="flex items-center justify-between gap-3">
              <span className="text-xs text-t3">{r.label}</span>
              <span className="text-xs font-medium text-t2 truncate max-w-[160px] text-right">{r.value}</span>
            </div>
          ))}
        </div>
        <div className="h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
        <div className="space-y-2">
          <Link
            to={`/conversations?repo=${repoId}&q=${encodeURIComponent(`Explain the ${node.label} service architecture`)}`}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs text-t3 hover:text-t1 transition-all border"
            style={{ borderColor: 'var(--b-subtle)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--b-strong)'; e.currentTarget.style.backgroundColor = 'var(--brand-dim)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--b-subtle)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Ask AI about this service
            <ChevronRight className="w-3 h-3 ml-auto" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ArchitecturePage() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [selectedRepoId, setSelectedRepoId] = useState<string>('');
  const [archData, setArchData] = useState<ArchData | null>(null);
  const [loadingArch, setLoadingArch] = useState(false);
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [reindexing, setReindexing] = useState(false);
  const { theme } = useTheme();

  const loadRepos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/repositories`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setRepos(data.data);
        // Auto-select the first indexed repo
        const firstIndexed = data.data.find((r: Repo) => r.indexingStatus === 'completed');
        if (firstIndexed) setSelectedRepoId(firstIndexed._id);
        else if (data.data.length > 0) setSelectedRepoId(data.data[0]._id);
      }
    } catch { /* ignore */ } finally {
      setLoadingRepos(false);
    }
  }, []);

  const triggerReindex = useCallback(async (repoId: string) => {
    setReindexing(true);
    try {
      await fetch(`${API_URL}/repositories/${repoId}/index`, {
        method: 'POST',
        headers: authHeaders(),
      });
      // Poll status
      const pollInterval = setInterval(async () => {
        const statusRes = await fetch(`${API_URL}/repositories/${repoId}/status`, { headers: authHeaders() });
        const statusData = await statusRes.json();
        if (statusData.success && statusData.data.indexingStatus === 'completed') {
          clearInterval(pollInterval);
          setReindexing(false);
          // Reload arch data
          const archRes = await fetch(`${API_URL}/repositories/${repoId}/architecture`, { headers: authHeaders() });
          const archJson = await archRes.json();
          if (archJson.success) setArchData(archJson.data);
          loadRepos();
        } else if (statusData.success && statusData.data.indexingStatus === 'failed') {
          clearInterval(pollInterval);
          setReindexing(false);
        }
      }, 3000);
    } catch {
      setReindexing(false);
    }
  }, [loadRepos]);

  useEffect(() => { loadRepos(); }, [loadRepos]);

  useEffect(() => {
    if (!selectedRepoId) { setArchData(null); return; }
    setLoadingArch(true);
    setSelectedNodeId(null);
    fetch(`${API_URL}/repositories/${selectedRepoId}/architecture`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success) setArchData(d.data); })
      .catch(() => {})
      .finally(() => setLoadingArch(false));
  }, [selectedRepoId]);

  const selectedRepo = repos.find(r => r._id === selectedRepoId);
  const nodes: GraphNode[] = archData ? layoutNodes(archData.services) : [];
  const edges = archData?.edges ?? [];
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
  const selectedNode = selectedNodeId ? nodeMap[selectedNodeId] ?? null : null;
  const edgeColor = theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const arrowFill  = theme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.20)';
  const selEdgeColor = theme === 'dark' ? '#FFFFFF' : '#000000';

  return (
    <div className="flex h-full">
      <SEO title="Architecture Explorer" description="Visualize your service dependencies and system architecture in real time. Understand connections, critical paths, and blast radius before making changes." noIndex />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="h-11 flex-shrink-0 border-b px-4 flex items-center gap-3" style={{ borderColor: 'var(--b-subtle)' }}>
          <Share2 className="w-4 h-4 text-t3 flex-shrink-0" />
          <span className="text-sm font-medium text-t1 flex-shrink-0">Architecture Explorer</span>

          {/* Repo selector */}
          {!loadingRepos && repos.length > 0 && (
            <div className="relative ml-2">
              <button
                onClick={() => setShowRepoDropdown(s => !s)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-all"
                style={{ backgroundColor: 'var(--s2)', borderColor: 'var(--b-default)', color: 'var(--t2)' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--b-strong)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--b-default)')}
              >
                <GitBranch className="w-3 h-3" />
                <span className="max-w-[160px] truncate">{selectedRepo?.name ?? 'Select repo'}</span>
                <ChevronDown className="w-3 h-3 text-t4" />
              </button>
              {showRepoDropdown && (
                <div className="absolute top-full left-0 mt-1 z-50 w-64 rounded-xl border shadow-surface-lg overflow-hidden"
                  style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-default)' }}>
                  {repos.map(r => (
                    <button
                      key={r._id}
                      onClick={() => { setSelectedRepoId(r._id); setShowRepoDropdown(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-left transition-colors"
                      style={r._id === selectedRepoId ? { backgroundColor: 'var(--s4)', color: 'var(--t1)' } : { color: 'var(--t2)' }}
                      onMouseEnter={e => { if (r._id !== selectedRepoId) e.currentTarget.style.backgroundColor = 'var(--overlay-sm)'; }}
                      onMouseLeave={e => { if (r._id !== selectedRepoId) e.currentTarget.style.backgroundColor = ''; }}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.indexingStatus === 'completed' ? 'bg-emerald-400' : r.indexingStatus === 'failed' ? 'bg-red-400' : 'bg-amber-400'}`} />
                      <span className="truncate">{r.name}</span>
                      <span className="ml-auto text-t4 capitalize text-[10px] flex-shrink-0">{r.indexingStatus}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loadingArch && archData?.analyzed && (
            <span className="text-xs text-t4 px-2 py-0.5 rounded font-mono" style={{ backgroundColor: 'var(--s4)' }}>
              {nodes.length} services · {edges.length} connections
            </span>
          )}

          <div className="ml-auto flex items-center gap-1">
            <button onClick={loadRepos} className="btn-ghost p-1.5" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-1 border-l pl-3" style={{ borderColor: 'var(--b-subtle)' }}>
              <button onClick={() => setZoom(z => Math.max(50, z - 10))} className="btn-ghost p-1.5"><ZoomOut className="w-3.5 h-3.5" /></button>
              <span className="text-xs text-t3 w-10 text-center">{zoom}%</span>
              <button onClick={() => setZoom(z => Math.min(150, z + 10))} className="btn-ghost p-1.5"><ZoomIn className="w-3.5 h-3.5" /></button>
              <button onClick={() => setZoom(100)} className="btn-ghost p-1.5"><Maximize2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto relative bg-dots" onClick={() => setShowRepoDropdown(false)}>
          {loadingRepos ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-t4" /></div>
          ) : repos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <Share2 className="w-10 h-10 text-t4 mb-4" />
              <h3 className="text-base font-semibold text-t1 mb-2">No repositories connected</h3>
              <p className="text-sm text-t3 max-w-xs leading-relaxed mb-6">Connect and index a repository to visualize its architecture here.</p>
              <Link to="/repositories" className="btn-primary"><Plus className="w-4 h-4" />Connect Repository</Link>
            </div>
          ) : !selectedRepoId ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-sm text-t3">Select a repository to view its architecture</p>
            </div>
          ) : loadingArch ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-t4" /></div>
          ) : !archData?.analyzed || nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="w-14 h-14 rounded-2xl border flex items-center justify-center mb-4"
                style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
                <Share2 className="w-7 h-7 text-t4" />
              </div>
              <h3 className="text-base font-semibold text-t1 mb-2">No architecture data yet</h3>
              <p className="text-sm text-t3 max-w-xs leading-relaxed mb-6">
                {selectedRepo?.indexingStatus !== 'completed'
                  ? 'This repository needs to be indexed first before architecture can be extracted.'
                  : 'Architecture data is extracted during indexing. Re-index this repository to extract services and connections.'}
              </p>
              <div className="flex gap-3">
                {selectedRepo?.indexingStatus === 'completed' && (
                  <button
                    onClick={() => triggerReindex(selectedRepoId)}
                    disabled={reindexing}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {reindexing
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Re-indexing…</>
                      : <><RefreshCw className="w-4 h-4" /> Re-index Now</>}
                  </button>
                )}
                <Link to="/repositories" className="btn-secondary">
                  <RefreshCw className="w-4 h-4" />
                  Go to Repositories
                </Link>
              </div>
            </div>
          ) : (
            <div
              className="relative"
              style={{ width: 780, height: 600, transform: `scale(${zoom / 100})`, transformOrigin: 'top left', margin: '20px' }}
            >
              <svg className="absolute inset-0" width={780} height={600} style={{ pointerEvents: 'none' }}>
                <defs>
                  <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L7,3 z" fill={arrowFill} />
                  </marker>
                  <marker id="arrow-async" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L7,3 z" fill="rgba(251,191,36,0.5)" />
                  </marker>
                </defs>
                {edges.map((e, i) => {
                  const na = nodeMap[e.from], nb = nodeMap[e.to];
                  if (!na || !nb) return null;
                  const isAsync = e.type === 'async';
                  const isSel = selectedNodeId === e.from || selectedNodeId === e.to;
                  return (
                    <g key={i}>
                      <line
                        x1={na.x} y1={na.y + 36}
                        x2={nb.x} y2={nb.y + 36}
                        stroke={isSel ? (isAsync ? '#FBBF24' : selEdgeColor) : edgeColor}
                        strokeWidth={isSel ? 1.5 : 1}
                        strokeDasharray={isAsync ? '4 3' : undefined}
                        markerEnd={`url(#arrow${isAsync ? '-async' : ''})`}
                      />
                      {e.label && isSel && (
                        <text x={(na.x + nb.x) / 2} y={(na.y + nb.y) / 2} textAnchor="middle"
                          fill="rgba(251,191,36,0.7)" fontSize="8" dy="-4">{e.label}</text>
                      )}
                    </g>
                  );
                })}
              </svg>

              {nodes.map(n => (
                <NodeCard
                  key={n.id}
                  node={n}
                  selected={selectedNodeId === n.id}
                  onClick={() => setSelectedNodeId(selectedNodeId === n.id ? null : n.id)}
                />
              ))}
            </div>
          )}

          {/* Legend */}
          {nodes.length > 0 && (
            <div className="absolute bottom-4 left-4 card p-3 space-y-1.5">
              <div className="text-[10px] text-t4 font-semibold uppercase tracking-wider mb-2">Types</div>
              {Array.from(new Set(nodes.map(n => n.type))).map(type => {
                const tc = getTypeConfig(type);
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tc.color }} />
                    <span className="text-[10px] text-t3">{tc.label}</span>
                  </div>
                );
              })}
              {edges.some(e => e.type === 'async') && (
                <>
                  <div className="h-px my-1" style={{ backgroundColor: 'var(--b-subtle)' }} />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-px border-t border-dashed border-amber-500/50" />
                    <span className="text-[10px] text-t4">Async</span>
                  </div>
                </>
              )}
            </div>
          )}

          {!selectedNodeId && nodes.length > 0 && (
            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-t3 border"
              style={{ backgroundColor: 'var(--glass-bg)', borderColor: 'var(--b-subtle)' }}>
              <Share2 className="w-3.5 h-3.5" />
              Click a node to inspect
            </div>
          )}
        </div>
      </div>

      {selectedNode && (
        <DetailPanel
          node={selectedNode}
          repoId={selectedRepoId}
          onClose={() => setSelectedNodeId(null)}
        />
      )}
    </div>
  );
}
